import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { CountBadge } from "@/components/shared/CountBadge";
import { exportKeywordsToCsv } from "@/utils/keywordExport";
import type { ExportMode, ExportFormat } from "@/utils/keywordExport";
import type { Keyword } from "@/types/keyword";
import type { KeywordTheme } from "@/types/keyword-theme";

interface ExportKeywordsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allKeywords: Keyword[];
  themes: KeywordTheme[];
}

export function ExportKeywordsModal({
  open,
  onOpenChange,
  allKeywords,
  themes,
}: ExportKeywordsModalProps) {
  const [scope, setScope] = useState<ExportMode>("all");
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [groupByTheme, setGroupByTheme] = useState<"no" | "yes">("no");

  const themedCount = allKeywords.filter((k) => {
    const themedIds = new Set(themes.flatMap((t) => t.keywords.map((tk) => tk.keyword_id)));
    return themedIds.has(k.id);
  }).length;
  const masterCount = allKeywords.filter((k) => k.is_master_list).length;

  const scopeCount =
    scope === "master" ? masterCount : scope === "themed" ? themedCount : allKeywords.length;

  const handleExport = () => {
    exportKeywordsToCsv(allKeywords, themes, scope, groupByTheme === "yes", format);
    toast({ title: `Exported ${scopeCount} keyword${scopeCount !== 1 ? "s" : ""}` });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export keywords</DialogTitle>
          <DialogDescription>
            Exporting {scopeCount} keyword{scopeCount !== 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold">Keywords to include</Label>
            <RadioGroup
              value={scope}
              onValueChange={(v) => setScope(v as ExportMode)}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="scope-all" />
                <Label htmlFor="scope-all" className="font-normal cursor-pointer inline-flex items-center gap-1.5">
                  All keywords <CountBadge size="sm" count={allKeywords.length} />
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="master" id="scope-master" />
                <Label htmlFor="scope-master" className="font-normal cursor-pointer inline-flex items-center gap-1.5">
                  Master List only <CountBadge size="sm" count={masterCount} />
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="themed" id="scope-themed" />
                <Label htmlFor="scope-themed" className="font-normal cursor-pointer inline-flex items-center gap-1.5">
                  Themed keywords only <CountBadge size="sm" count={themedCount} />
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold">File format</Label>
            <RadioGroup
              value={format}
              onValueChange={(v) => setFormat(v as ExportFormat)}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="csv" id="fmt-csv" />
                <Label htmlFor="fmt-csv" className="font-normal cursor-pointer">CSV (.csv)</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="tsv" id="fmt-tsv" />
                <Label htmlFor="fmt-tsv" className="font-normal cursor-pointer">
                  TSV (.tsv) — Excel / Sheets
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="json" id="fmt-json" />
                <Label htmlFor="fmt-json" className="font-normal cursor-pointer">JSON (.json)</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-semibold">Group by theme</Label>
            <RadioGroup
              value={groupByTheme}
              onValueChange={(v) => setGroupByTheme(v as "no" | "yes")}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="grp-no" />
                <Label htmlFor="grp-no" className="font-normal cursor-pointer">
                  No — flat list
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="grp-yes" />
                <Label htmlFor="grp-yes" className="font-normal cursor-pointer">
                  Yes — group by theme (unthemed last)
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport}>
            <Download className="h-4 w-4" />
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
